/* eslint-disable react/no-array-index-key */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import { useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ProductCarouselProductStyles } from "@insite/content-library/Components/ProductCarouselProduct";
import { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import FlexItem from "@insite/content-library/Widgets/Brand/FlexItem";
import FlexWrapContainer from "@insite/content-library/Widgets/Brand/FlexWrapContainer";
import Button, { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage from "@insite/mobius/LazyImage";
import Link from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import EmblaCarousel from "@sunSource/Components/EmblaCarousal";
import ChevronLeft from "@sunSource/Icons/ChevronLeft";
import ChevronRight from "@sunSource/Icons/ChevronRight";
import { useEmblaCarousel } from "embla-carousel/react";
import React, { FC } from "react";
import { css, ThemeProps, withTheme } from "styled-components";

interface SlideModel {
    fields: {
        Title: string;
        image: string;
        description: string;
        link: LinkFieldValue;
        linkTitle: string;
        dateTitle: string;
        timelineTitle: string;
    };
}

const enum fields {
    slides = "slides",
    slidesPerRow = "slidesPerRow",
    title = "title",
    hideCompanyData = "hideCompanyData"
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.slides]: SlideModel[];
        [fields.slidesPerRow]: number;
        [fields.title]: string;
        [fields.hideCompanyData]: boolean;
    };
    extendedStyles?: SlideshowStyles;
}

type Props = OwnProps & ThemeProps<BaseTheme> & HasShellContext;
export interface SlideshowStyles {
    wrapper?: InjectableCss;
    imageItem?: InjectableCss;
    companyContentWrapper?: InjectableCss;
    companyTitle?: TypographyProps;
    companyDesc?: TypographyProps;
    infoCardTitle?: TypographyProps;
    companyImage?: InjectableCss;
    content?: InjectableCss;
    images?: InjectableCss;
    linkStyles?: InjectableCss;
    errorMessageText?: TypographyPresentationProps;
    titleText?: TypographyPresentationProps;
    skipCarouselButton?: SkipNavStyles;
    mainContainer?: GridContainerProps;
    prevArrowGridItem?: GridItemProps;
    prevArrowButton?: ButtonPresentationProps;
    nextArrowGridItem?: GridItemProps;
    nextArrowButton?: ButtonPresentationProps;
    carouselGridItem?: GridItemProps;
    carouselWrapper?: InjectableCss;
    carouselContainer?: InjectableCss;

    carouselSlidesContainer?: InjectableCss;
    carouselSlide?: InjectableCss;
    carouselSlideInner?: InjectableCss;
    carouselProductStyles?: ProductCarouselProductStyles;
    mainWrapper?: InjectableCss;
    verticalLine?: InjectableCss;
    dateTitle?: InjectableCss;
    timelineTitle?: InjectableCss;
}

export const slideshowStyles: SlideshowStyles = {
    wrapper: {
        css: css`
            margin: 50px auto 0;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            max-width: 100% !important;
                            padding: 20px;
                            flex-wrap: wrap;
                        `,
                        css`
                            max-width: 100% !important;
                            padding: 20px;
                            flex-wrap: wrap;
                        `,
                        css`
                            max-width: 720px !important;
                            flex-wrap: wrap;
                        `,
                        css`
                            max-width: 960px !important;
                            padding: 0px;
                            flex-wrap: nowrap;
                        `,
                        css`
                            max-width: 1300px !important;
                            flex-wrap: nowrap;
                            padding: 0px;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    infoCardTitle: {
        variant: "h2",
        css: css`
            text-align: center;
            text-transform: uppercase;
            font-size: 2rem;
            margin: 30px 0 10px;
            color: ${getColor("primary.main")};
        `,
    },
    imageItem: {
        css: css`
            padding: 5px 25px;
            justify-content: center;
            display: flex;
        `,
    },
    companyContentWrapper: {
        css: css`
            margin-top: 35px;
            display: flex;
            padding: 30px 0;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            flex-wrap: wrap;
                        `,
                        null,
                        css`
                            flex-wrap: nowrap;
                        `,
                        null,
                    ],
                    "min",
                )}
        `,
    },
    companyTitle: {
        variant: "h3",
        css: css`
            text-transform: uppercase;
            font-size: 2rem;
            font-weight: 600;
            margin-bottom: 30px;
        `,
    },
    content: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            width: 100%;
                        `,
                        css`
                            width: 60%;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    images: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            width: 100%;
                        `,
                        css`
                            width: 40%;
                        `,
                    ],
                    "min",
                )}
            display: flex;
            justify-content: center;
            align-items: center;
            > div:first-child {
                display: flex;
                flex-wrap: nowrap;
                flex-direction: column;
                width: 80%;

                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                margin-top: 50px;
                            `,
                            null,
                            css`
                                margin-top: -50px;
                            `,
                        ],
                        "min",
                    )}
                > div[class^="LazyImageStyle-sc-"] {
                    overflow: initial;
                }
            }
            > div:first-child img {
                width: 70%;
            }
        `,
    },
    companyDesc: {
        css: css`
            line-height: 30px;
        `,
    },
    linkStyles: {
        css: css`
            border: 1px solid;
            width: 75%;
            text-align: center;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 9px 0;
            margin: 35px auto 0;
            text-transform: uppercase;
            font-weight: 600;
            background-color: #fff;
            border: 1px solid ${getColor("common.backgroundContrast")};
        `,
    },
    mainWrapper: {
        css: css`
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        css`
                            padding: 5px 35px 5px 35px;
                        `,
                        null,
                        null,
                        css`
                            padding: 0px 95px 0px 95px;
                        `,
                    ],
                    "min",
                );
            }}
        `,
    },
    errorMessageText: {
        color: "danger",
        css: css`
            margin-top: 20px;
        `,
    },
    titleText: {
        variant: "h2",
        css: css`
            text-align: center;
            margin-bottom: 10px;
            color: ${getColor("text.accent")};
        `,
    },
    mainContainer: {
        gap: 0,
        css: css`
            position: relative;
        `,
    },
    prevArrowGridItem: {
        width: 1,
        align: "middle",
        css: css`
            position: absolute;
            top: 45%;
            border-radius: 50%;
            background: transparent;
            left: -9px;
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1;
            max-width: none;
            width: 40px;
            height: 40px;
        `,
    },
    prevArrowButton: {
        variant: "secondary",
        color: "text.main",
        css: css`
            span {
                width: 30px;
                height: 30px;
            }
            svg {
                width: 30px;
                height: 30px;
            }
            border: 0;
            padding: 0;
            &:focus {
                outline: none;
            }
        `,
    },
    nextArrowGridItem: {
        width: 1,
        align: "middle",
        css: css`
            position: absolute;
            top: 45%;
            right: -9px;
            border-radius: 50%;
            background: transparent;
            display: flex;
            align-items: center;
            justify-content: center;
            max-width: none;
            width: 40px;
            height: 40px;
        `,
    },
    nextArrowButton: {
        variant: "secondary",
        color: "text.main",
        css: css`
            border: 0;
            padding: 0;
            span {
                width: 30px;
                height: 30px;
            }
            svg {
                //stroke: white;
                width: 30px;
                height: 30px;
            }
            &:focus {
                outline: none;
            }
        `,
    },
    carouselGridItem: {
        width: 10,
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                    css`
                        flex-basis: 100%;
                        max-width: 100%;
                    `,
                ])}
        `,
    },
    carouselWrapper: {
        css: css`
            width: 100%;
            position: relative;
        `,
    },
    carouselContainer: {
        css: css`
            overflow: hidden;
            padding: 20px 30px 0;
        `,
    },
    carouselSlidesContainer: {
        css: css`
            display: flex;
            padding-top: 10px;
            padding-bottom: 10px;
        `,
    },
    carouselSlide: {
        css: css`
            background: transparent;
            padding: 0;
            position: relative;
            flex: 0 0 auto;
            border-radius: 12px;
            padding: 0 10px;
        `,
    },
    carouselSlideInner: {
        css: css`
            [data-test-selector="productImage"] {
                min-height: 150px;
                max-height: 150px;
                height: 150px;
            }
            width: 100%;
            height: 100%;
            position: relative;
            padding: 15px 20px;
            display: flex;
            flex-direction: column;
            cursor: pointer;
            [data-test-selector="productImage"] ~ div {
                margin: 15px 0 7px 0;
                [class^="TypographyStyle"] {
                    font-size: 14px;
                }
            }
            [data-test-selector="productDescriptionLink"] > span {
                font-size: 15px;
                text-align: center;
                color: ${getColor("text.accent")};
                font-weight: 500;
                &:hover {
                    color: ${getColor("text.accent")};
                }
            }
        `,
    },
    carouselProductStyles: {
        productBrandStyles: {
            nameLink: {
                css: css`
                    [class^="TypographyStyle"] {
                        font-weight: 400 !important;
                    }
                `,
            },
        },
        productImageStyles: {
            linkWrappingImage: {
                css: css`
                    display: flex;
                    justify-content: center;
                `,
            },
            img: {
                css: css`
                    max-width: 100%;
                    max-height: 150px;
                    width: 150px;
                `,
            },
        },
    },
    verticalLine: {
        css: css`
            border-left: 2px solid #E7E6EC;
            height: 70px;
            position: relative;
            left: 50%;
        `
    },
    dateTitle: {
        css: css`
           color: #2E64B0;
           font-size: 20px;
           text-align: center;
           font-family: Barlow-Bold;
        `
    },
    timelineTitle: {
        css: css`
           text-align: center;
           font-family: Barlow-Bold;
        `
    }
};
const styles = slideshowStyles;
export const SunSourceCompanies: FC<Props> = ({ fields, shellContext, theme }) => {
    const companyLink = useGetLinks(fields.slides, o => o.fields.link);

    const [companyData, setCompanyData] = React.useState<number>(0);

    const desktop = parseInt(`${fields.slidesPerRow}`, 10);
    const tablet = Math.ceil(desktop / 2);
    const mobile = Math.ceil(tablet / 2);

    const [emblaRef, embla] = useEmblaCarousel();
    const [windowResizeTime, setWindowResizeTime] = React.useState(0);
    const [canScrollPrev, setCanScrollPrev] = React.useState(false);
    const [canScrollNext, setCanScrollNext] = React.useState(false);
    const setCanScroll = () => {
        if (shellContext.isInShell) {
            return;
        }
        setCanScrollPrev(!!embla && embla.canScrollPrev());
        setCanScrollNext(!!embla && embla.canScrollNext());
    };

    const getSlidesToScroll = React.useCallback(() => {
        const maxNumberOfColumns = fields.slidesPerRow || 4;
        if (typeof window === "undefined") {
            return maxNumberOfColumns;
        }

        let localSlidesToScroll: number;
        if (window.innerWidth < theme.breakpoints.values[1]) {
            localSlidesToScroll = 1;
        } else if (window.innerWidth < theme.breakpoints.values[2]) {
            localSlidesToScroll = 2;
        } else if (window.innerWidth < theme.breakpoints.values[3]) {
            localSlidesToScroll = 3;
        } else if (window.innerWidth < theme.breakpoints.values[4]) {
            localSlidesToScroll = 4;
        } else {
            localSlidesToScroll = 5;
        }
        return Math.min(localSlidesToScroll, maxNumberOfColumns);
    }, [fields.slidesPerRow]);

    const [slidesToScroll, setSlidesToScroll] = React.useState(getSlidesToScroll());

    const getDraggable = () => {
        if (typeof window === "undefined") {
            return false;
        }

        return window.innerWidth < theme.breakpoints.values[1];
    };

    const [draggable, setDraggable] = React.useState(getDraggable());

    React.useEffect(() => {
        const newSlidesToScroll = getSlidesToScroll();
        setSlidesToScroll(newSlidesToScroll);

        const newDraggable = getDraggable();
        setDraggable(newDraggable);
    }, [fields.slidesPerRow, windowResizeTime]);

    React.useEffect(() => {
        if (!embla) {
            return;
        }

        embla.on("init", setCanScroll);
        embla.on("select", setCanScroll);

        const onWindowResize = () => {
            setWindowResizeTime(Date.now());
        };

        window.addEventListener("resize", onWindowResize);
        onWindowResize();

        return () => {
            window.removeEventListener("resize", onWindowResize);
        };
    }, [embla]);

    React.useEffect(() => {
        if (!embla || !fields.slides.length) {
            return;
        }

        embla.reInit({
            align: "start",
            slidesToScroll,
            draggable,
            loop: fields.slides.length > slidesToScroll,
        });
        setCanScroll();
    }, [embla, slidesToScroll, draggable, fields.slides]);

    const showCompanyData = () =>
        fields.slides
            .filter((item: SlideModel, index: number) => index === companyData)
            .map((item: SlideModel, index: number) => (
                <StyledWrapper key={item.fields.Title} {...styles.companyContentWrapper}>
                    <StyledWrapper {...styles.content}>
                        <Typography {...styles.companyTitle}>{item.fields.Title}</Typography>
                        <Typography {...styles.companyDesc}>{item.fields.description}</Typography>
                    </StyledWrapper>
                    <StyledWrapper {...styles.images}>
                        <StyledWrapper>
                            <LazyImage src={item.fields.image} />
                            {companyLink[companyData].url ? (
                                <Link href={companyLink[companyData].url} target="_blank" {...styles.linkStyles}>
                                    {item.fields.linkTitle}
                                </Link>
                            ) : null}
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
            ));

    const showCompanyLogos = React.useCallback(() => {
        return fields.slides.map((item: SlideModel, index: number) => (
            <FlexItem
                key={item.fields.Title}
                flexColumns={[mobile, tablet, tablet, desktop, desktop]}
                {...styles.imageItem}
                style={{
                    width: `calc(100% / ${fields.slidesPerRow})`,
                    minWidth: `calc(100% / ${fields.slidesPerRow})`,
                }}
            >
                <LazyImage src={item.fields.image} onClick={() => setCompanyData(index)} />
            </FlexItem>
        ));
    }, [companyData]);

    return (
        <StyledWrapper>
            <StyledWrapper {...styles.wrapper}>
                {fields.title ? <Typography {...styles.infoCardTitle}>{fields.title}</Typography> : null}

                <GridContainer {...styles.mainContainer}>
                    <GridItem {...styles.prevArrowGridItem}>
                        <Button
                            {...styles.prevArrowButton}
                            onClick={() => embla && embla.scrollPrev()}
                            disabled={!canScrollPrev}
                            data-test-selector="prevBtn"
                        >
                            <ButtonIcon src={ChevronLeft} />
                        </Button>
                    </GridItem>

                    <GridItem {...styles.carouselGridItem}>
                        <StyledWrapper {...styles.carouselWrapper}>
                            <StyledWrapper {...styles.carouselContainer} ref={emblaRef}>
                                <StyledWrapper {...styles.carouselSlidesContainer} data-test-selector="slides">
                                    {fields.slides.map((item: SlideModel, index: number) => (
                                        <StyledWrapper
                                            {...styles.carouselSlide}
                                            key={`${item.fields.Title}${index}`}
                                            style={{ width: `calc(100% / ${slidesToScroll})` }}
                                        >
                                            <StyledWrapper
                                                {...styles.carouselSlideInner}
                                                data-test-selector="carousel_slide"
                                            >
                                                <StyledWrapper {...styles.dateTitle}>{item.fields.dateTitle}</StyledWrapper>
                                                <LazyImage
                                                    src={item.fields.image}
                                                    onClick={() => setCompanyData(index)}
                                                />
                                                {item.fields.timelineTitle && <StyledWrapper {...styles.verticalLine}></StyledWrapper>}
                                                <StyledWrapper {...styles.timelineTitle}>{item.fields.timelineTitle}</StyledWrapper>
                                            </StyledWrapper>
                                        </StyledWrapper>
                                    ))}
                                </StyledWrapper>
                            </StyledWrapper>
                        </StyledWrapper>
                    </GridItem>

                    <GridItem {...styles.nextArrowGridItem}>
                        <Button
                            {...styles.nextArrowButton}
                            onClick={() => embla && embla.scrollNext()}
                            disabled={!canScrollNext}
                            data-test-selector="nextBtn"
                        >
                            <ButtonIcon src={ChevronRight} />
                        </Button>
                    </GridItem>
                </GridContainer>
                <StyledWrapper>{showCompanyData()}</StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const contentTab = {
    displayName: "Content",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 2,
};

const widgetModule: WidgetModule = {
    component: withIsInShell(withTheme(SunSourceCompanies)),
    definition: {
        group: "Basic",
        icon: "presentation-screen",
        fieldDefinitions: [
            {
                name: fields.slidesPerRow,
                editorTemplate: "IntegerField",
                defaultValue: 4,
                fieldType: "General",
                displayName: "Slides Per Row(Min 2 & max 5) ",
                tab: advancedTab,
                min: 2,
                max: 5,
                placeholder: "Minumun 2 & Maximun 5",
            },

            {
                name: fields.title,
                editorTemplate: "TextField",
                displayName: "Title",
                defaultValue: "",
                tab: contentTab,
                fieldType: "Translatable",
            },

            {
                name: fields.slides,
                editorTemplate: "ListField",
                tab: contentTab,
                getDisplay: ({ fields: { Title } }) => Title || "Slide",
                defaultValue: [],
                fieldType: "Translatable",
                hideAdd: false,

                fieldDefinitions: [
                    {
                        name: "Title",
                        editorTemplate: "TextField",
                        displayName: "Comapny Name",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: "dateTitle",
                        editorTemplate: "TextField",
                        displayName: "Date Title",
                        defaultValue: "",
                        isRequired: false,
                    },
                    {
                        name: "timelineTitle",
                        editorTemplate: "TextField",
                        displayName: "Timeline Title",
                        defaultValue: "",
                        isRequired: false,
                    },
                    {
                        name: "image",
                        editorTemplate: "ImagePickerField",
                        displayName: "Comapny Logo",
                        defaultValue: "",
                        isRequired: true,
                    },

                    {
                        name: "description",
                        displayName: "Description",
                        editorTemplate: "MultilineTextField",
                        defaultValue: "",
                    },
                    {
                        name: "link",
                        displayName: "Link",
                        editorTemplate: "LinkField",
                        defaultValue: { type: "Url", value: "" },
                        // allowUrls: item => false,
                        // allowCategories: item => false,
                    },

                    {
                        name: "linkTitle",
                        displayName: "Link Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                ],
            },
            {
                name: fields.hideCompanyData,
                displayName: "Hide Company Data",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tab: contentTab,
            },
        ],
    },
};

export default widgetModule;
